import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Clock, Volume2, MessageSquare } from 'lucide-react';
import { formatKurus, NOISE_WARNING_TR } from '@yalidavet/shared';
import { api } from '@/lib/api';
import { formatDateTr } from '@/lib/utils';

export default function BookingSuccessPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const ref = params.get('ref');

  const { data: booking } = useQuery({
    queryKey: ['booking', id],
    queryFn: () => api.booking(id!),
    enabled: !!id,
  });

  return (
    <div className="yd-container max-w-2xl py-12" data-testid="booking-success">
      <div className="yd-card p-8 text-center">
        <CheckCircle2 className="mx-auto mb-4 h-16 w-16 text-emerald-500" aria-hidden />
        <h1 className="font-display text-2xl font-bold text-ink-900">Rezervasyon Talebiniz Alındı</h1>
        <p className="mt-2 text-ink-500">
          Ev sahibi talebinizi en geç 24 saat içinde yanıtlayacaktır.
        </p>

        <p className="mt-5 inline-block rounded-lg bg-bosphorus-50 px-4 py-2 font-mono text-sm font-semibold text-bosphorus-800" data-testid="booking-reference">
          {booking?.referenceCode ?? ref ?? '—'}
        </p>

        {booking && (
          <dl className="mt-6 space-y-2 border-t border-ink-100 pt-6 text-left text-sm">
            <Row label="Mekan" value={booking.mansionTitle} />
            <Row label="Tarihler" value={`${formatDateTr(booking.checkinDate)} – ${formatDateTr(booking.checkoutDate)}`} />
            <Row label="Misafir Sayısı" value={`${booking.guestCount} kişi`} />
            <Row label="Çıkış Saati" value={booking.checkoutTime} />
            <Row label="Hasar Depozitosu" value={formatKurus(booking.depositKurus)} />
            <Row label="Toplam Tutar" value={formatKurus(booking.totalKurus)} bold />
          </dl>
        )}

        {/* Hard-stop reminders */}
        <div className="mt-6 space-y-2 text-left">
          <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
            <Clock className="h-4 w-4 shrink-0" aria-hidden />
            Etkinlik en geç <strong>{booking?.checkoutTime ?? '23:30'}</strong> itibarıyla sonlandırılmalıdır.
          </p>
          <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900" data-testid="success-noise-warning">
            <Volume2 className="h-4 w-4 shrink-0" aria-hidden />
            {NOISE_WARNING_TR(booking?.checkoutTime ?? '23:30', '23:30')}
          </p>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/mesajlar" className="yd-btn-primary">
            <MessageSquare className="mr-1.5 h-4 w-4" /> Ev Sahibiyle Mesajlaş
          </Link>
          <Link to="/ara" className="yd-btn-secondary">Aramaya Dön</Link>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value?: string; bold?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-500">{label}</dt>
      <dd className={bold ? 'font-semibold text-ink-900' : 'text-ink-800'}>{value ?? '—'}</dd>
    </div>
  );
}
